import { APIClient } from '../../helpers/api_helper';
import TokenManager from '../../utils/tokenManager';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// =========================
// Types and Interfaces
// =========================

export interface PaymentTransaction {
  _id: string;
  amount: number;
  method: string;
  milestone: string;
  type: string;
  playersCount: number;
  paidStatus: 'paid' | 'failed' | 'pending';
  paidAt: string;
  createdAt: string;
  paymentId?: string;
  tournamentInfo: {
    _id: string;
    name: string;
    shortName: string;
    logo: string;
    ageCategory?: string;
  };
  teamInfo: {
    _id: string;
    name: string;
    shortName: string;
    logo: string;
    ageCategory: string;
    clubInfo?: {
      _id: string;
      name: string;
      logo: string;
      address?: string;
      gstNum?: string;  // API uses gstNum
    };
  };
}

export interface PaymentStats {
  totalRevenue: number;
  realizedProfit: number;
  paymentDues: number;
}

export interface AllTransactionsResponse {
  statusCode: number;
  status: string;
  message: string;
  data: {
    paginatedResults: PaymentTransaction[];
    totalCount: number;
  };
}

export interface PaymentFilters {
  ageCategory?: string;
  status?: string;
  teams?: string;
  tournaments?: string;
}

// =========================
// API Functions
// =========================

/**
 * Get all payment transactions with pagination
 * @param pageNum - Page number for pagination (default: 1)
 * @param filters - Optional filters
 * @returns Promise<AllTransactionsResponse>
 */
export const getAllTransactions = async (
  pageNum: number = 1,
  filters?: PaymentFilters
): Promise<AllTransactionsResponse> => {
  try {
    if (!API_BASE_URL) {
      throw new Error('API_BASE_URL not configured');
    }
    
    const authToken = TokenManager.getToken();
    if (!authToken) {
      throw new Error('No authentication token found');
    }

    // Build query parameters
    const params = new URLSearchParams();
    params.append('pageNum', pageNum.toString());
    
    if (filters) {
      if (filters.ageCategory && filters.ageCategory !== 'All') {
        params.append('ageCategory', filters.ageCategory);
      }
      if (filters.status && filters.status !== 'All') {
        params.append('status', filters.status);
      }
      if (filters.teams && filters.teams !== 'All') {
        params.append('teams', filters.teams);
      }
      if (filters.tournaments && filters.tournaments !== 'All') {
        params.append('tournaments', filters.tournaments);
      }
    }

    const apiClient = new APIClient();
    
    // Debug logging to match Postman request
    console.log('🔍 API Debug - Making request to:', `${API_BASE_URL}/api/v1/payments/tournament-registration/all-transactions?${params.toString()}`);
    console.log('🔍 API Debug - Auth token exists:', !!authToken);
    console.log('🔍 API Debug - Auth token length:', authToken.length);
    console.log('🔍 API Debug - Auth token preview:', authToken.substring(0, 50) + '...');
    
    const response = await apiClient.get(
      `${API_BASE_URL}/api/v1/payments/tournament-registration/all-transactions?${params.toString()}`,
      null
    );
    
    console.log('🔍 API Debug - Response received:', response);
    console.log('🔍 API Debug - Response structure:', {
      hasData: !!response.data,
      hasPaginatedResults: !!response.data?.paginatedResults,
      totalCount: response.data?.totalCount,
      statusCode: response.data?.statusCode
    });
    
    // Handle the API response structure - based on actual API response
    if (response && response.data) {
      console.log('🔍 API Debug - Response structure:', {
        hasData: !!response.data,
        hasPaginatedResults: !!response.data.paginatedResults,
        hasNestedData: !!response.data.data,
        totalCount: response.data.totalCount,
        statusCode: response.data.statusCode
      });
      
      // Based on the actual API response structure:
      // {
      //   "statusCode": 200,
      //   "status": "success", 
      //   "message": "Payments fetched successfully",
      //   "data": {
      //     "paginatedResults": [...],
      //     "totalCount": 11
      //   }
      // }
      
      // Pattern 1: Standard API response structure (most common)
      if (response.data.statusCode && response.data.data && response.data.data.paginatedResults) {
        console.log('🔍 API Debug - Using standard API response structure');
        return {
          statusCode: response.data.statusCode,
          status: response.data.status,
          message: response.data.message,
          data: {
            paginatedResults: response.data.data.paginatedResults,
            totalCount: response.data.data.totalCount
          }
        };
      }
      
      // Pattern 2: Direct paginated response (like manage tournaments)
      if (response.data.paginatedResults && typeof response.data.totalCount === 'number') {
        console.log('🔍 API Debug - Using direct paginated response structure');
        return {
          statusCode: response.data.statusCode || 200,
          status: response.data.status || 'success',
          message: response.data.message || 'Data retrieved successfully',
          data: {
            paginatedResults: response.data.paginatedResults,
            totalCount: response.data.totalCount
          }
        };
      }
      
      // Pattern 3: Array response (fallback)
      if (Array.isArray(response.data)) {
        console.log('🔍 API Debug - Using array response structure');
        return {
          statusCode: 200,
          status: 'success',
          message: 'Data retrieved successfully',
          data: {
            paginatedResults: response.data,
            totalCount: response.data.length
          }
        };
      }
      
      // Pattern 4: Direct response structure (fallback)
      console.log('🔍 API Debug - Using direct response structure');
      return response.data;
    }
    
    console.error('🔍 API Debug - Invalid response structure:', response);
    throw new Error('Invalid response structure from all transactions API');
  } catch (error) {
    console.error('Failed to fetch all transactions:', error);
    throw error;
  }
};

/**
 * Calculate payment statistics from transactions data
 * @param transactions - Array of payment transactions
 * @returns PaymentStats
 */
export const calculatePaymentStats = (transactions: PaymentTransaction[]): PaymentStats => {
  const stats = transactions.reduce(
    (acc, transaction) => {
      // Total Revenue: Sum of all paid amounts
      if (transaction.paidStatus === 'paid') {
        acc.totalRevenue += transaction.amount;
      }
      
      // Realized Profit: Sum of paid amounts (simplified calculation)
      if (transaction.paidStatus === 'paid') {
        acc.realizedProfit += transaction.amount;
      }
      
      // Payment Dues: Sum of pending and failed amounts
      if (transaction.paidStatus === 'pending' || transaction.paidStatus === 'failed') {
        acc.paymentDues += transaction.amount;
      }
      
      return acc;
    },
    { totalRevenue: 0, realizedProfit: 0, paymentDues: 0 }
  );

  return stats;
};

/**
 * Get unique age categories from transactions
 * @param transactions - Array of payment transactions
 * @returns string[]
 */
export const getUniqueAgeCategories = (transactions: PaymentTransaction[]): string[] => {
  const categories = new Set<string>();
  transactions.forEach(transaction => {
    // Extract age category from tournament name or type
    if (transaction.tournamentInfo.name) {
      const match = transaction.tournamentInfo.name.match(/U\d+/);
      if (match) {
        categories.add(match[0]);
      }
    }
  });
  return Array.from(categories).sort();
};

/**
 * Get unique teams from transactions
 * @param transactions - Array of payment transactions
 * @returns string[]
 */
export const getUniqueTeams = (transactions: PaymentTransaction[]): string[] => {
  const teams = new Set<string>();
  transactions.forEach(transaction => {
    if (transaction.teamInfo.name) {
      teams.add(transaction.teamInfo.name);
    }
  });
  return Array.from(teams).sort();
};

/**
 * Get unique tournaments from transactions
 * @param transactions - Array of payment transactions
 * @returns string[]
 */
export const getUniqueTournaments = (transactions: PaymentTransaction[]): string[] => {
  const tournaments = new Set<string>();
  transactions.forEach(transaction => {
    if (transaction.tournamentInfo.name) {
      tournaments.add(transaction.tournamentInfo.name);
    }
  });
  return Array.from(tournaments).sort();
}; 